import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { Loader2, Wand2, RefreshCw, Download, Paintbrush, ImageOff, Archive } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useI18n } from "@/i18n";
import { api, fileUrl, downloadBlob } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/common";
import UploadBox from "@/app/tool/UploadBox";
import { HeroOptions, ModelOptions, LifestyleOptions, DimensionsOptions, BgPicker, Field } from "@/app/tool/Options";
import ExportDialog from "@/app/ExportDialog";
import ArrowEditor from "@/app/tool/ArrowEditor";

const DEFAULTS = {
  size: "1:1", bg_hex: "#FFFFFF", shadow: true, ai_enhance: true, lighting: "soft", category: "necklace", model_id: null,
  model_preset: "female_natural", framing: "close", background: "", style: "studio", mood: "calm", scene_notes: "",
  dims: [{ key: "width", value: "" }, { key: "height", value: "" }], unit: "cm", label_lang: "tr", title: "", remove_bg: true, with_model: false,
};
const OPTIONS = { hero: HeroOptions, model: ModelOptions, lifestyle: LifestyleOptions, dimensions: DimensionsOptions };
const TERMINAL = ["completed", "failed", "canceled"];

function useJobPoller(onDone) {
  const timer = useRef(null);
  const stop = () => clearTimeout(timer.current);
  const poll = useCallback((jobId, setJob) => {
    stop();
    const tick = async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        setJob(data);
        if (TERMINAL.includes(data.status)) return onDone(data);
      } catch { /* transient — keep polling */ }
      timer.current = setTimeout(tick, 2500);
    };
    tick();
  }, [onDone]);
  useEffect(() => stop, []);
  return poll;
}

function ResultView({ project, tool, onRefresh }) {
  const { t, errMsg } = useI18n();
  const [active, setActive] = useState(0);
  const [exportId, setExportId] = useState(null);
  const [sel, setSel] = useState([]);
  const [bg, setBg] = useState(project.settings.bg_hex || "#FFFFFF");
  const [bgBusy, setBgBusy] = useState(false);
  const { config } = useOutletContext();
  const results = project.results || [];
  useEffect(() => setActive(0), [results.length]);
  const r = results[active];
  if (!r) return null;

  const changeBg = async () => {
    setBgBusy(true);
    try {
      await api.post(`/projects/${project.id}/results/${r.id}/background`, { bg_hex: bg, shadow: project.settings.shadow });
      toast.success(t("tool.bgChanged"));
      onRefresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBgBusy(false); }
  };
  const zip = async () => {
    try { await downloadBlob("/export/zip", null, "post", { result_ids: sel, fmt: "webp", preset: "web" }); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="light-panel overflow-hidden" style={{ background: r.bg_hex || "#F8FAFC" }}>
        <img src={fileUrl(r.master_file_id)} alt={project.name} className="w-full max-h-[620px] object-contain" data-testid="result-image" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-neutral-400 font-mono mr-auto" data-testid="result-dimensions">{r.width}×{r.height}px{r.bg_hex ? ` · ${r.bg_hex}` : ""}</span>
        <button onClick={() => setExportId(r.id)} className="btn-brand rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2" data-testid="result-download-btn"><Download size={15} />{t("common.download")}</button>
      </div>
      {tool === "hero" && r.cutout_file_id && (
        <div className="panel p-4 space-y-3" data-testid="change-bg-panel">
          <p className="text-sm font-medium flex items-center gap-2"><Paintbrush size={15} />{t("tool.changeBg")}</p>
          <BgPicker value={bg} onChange={setBg} presets={config.bg_presets} idPrefix="change-bg" />
          <button onClick={changeBg} disabled={bgBusy} className="rounded-full border border-emerald-600 text-emerald-400 px-4 py-2 text-sm hover:bg-emerald-600/10 disabled:opacity-60" data-testid="apply-bg-btn">
            {bgBusy ? <Loader2 size={15} className="animate-spin inline" /> : t("tool.applyBg")}
          </button>
          <p className="text-[11px] text-neutral-500">{t("tool.freeNote")}</p>
        </div>
      )}
      {tool === "dimensions" && r.product_file_id && r.arrows?.length > 0 && <ArrowEditor project={project} result={r} onSaved={onRefresh} />}
      {results.length > 1 && (
        <div>
          <p className="text-sm font-medium mb-2">{t("tool.results")}</p>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            {results.map((x, i) => (
              <div key={x.id} className="relative">
                <button onClick={() => setActive(i)} className={`w-full aspect-square rounded-lg overflow-hidden border-2 bg-white ${i === active ? "border-emerald-500" : "border-transparent"}`} data-testid={`result-thumb-${i}`}>
                  <img src={fileUrl(x.thumb_file_id)} alt="" loading="lazy" className="w-full h-full object-contain" />
                </button>
                <Checkbox className="absolute top-1.5 left-1.5 bg-black/60" checked={sel.includes(x.id)} aria-label={t("tool.selectForZip")}
                  onCheckedChange={(c) => setSel(c ? [...sel, x.id] : sel.filter((s) => s !== x.id))} data-testid={`result-select-${i}`} />
              </div>
            ))}
          </div>
          {sel.length > 0 && <button onClick={zip} className="mt-3 text-sm text-emerald-400 flex items-center gap-2" data-testid="zip-download-btn"><Archive size={15} />{t("tool.zip", { n: sel.length })}</button>}
        </div>
      )}
      <ExportDialog resultId={exportId} open={!!exportId} onOpenChange={(o) => !o && setExportId(null)} />
    </div>
  );
}

export default function ToolPage({ tool }) {
  const { t, errMsg, lang } = useI18n();
  const { config, ent, reloadEnt } = useOutletContext();
  const [params, setParams] = useSearchParams();
  const [fileId, setFileId] = useState(null);
  const [name, setName] = useState("");
  const [s, setS] = useState(() => ({ ...DEFAULTS, label_lang: lang }));
  const langRef = useRef(lang);
  langRef.current = lang;
  const [project, setProject] = useState(null);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const pid = params.get("project");
  const Options = OPTIONS[tool];

  const loadProject = useCallback(async (id) => {
    const { data } = await api.get(`/projects/${id}`);
    setProject(data.project);
    setFileId(data.project.source_file_id);
    setName(data.project.name);
    setS({ ...DEFAULTS, ...data.project.settings, dims: data.project.settings.dims?.length ? data.project.settings.dims : DEFAULTS.dims });
    return data;
  }, []);

  const onDone = useCallback((j) => {
    reloadEnt();
    if (j.status === "failed") toast.error(t("errors.GEN_FAILED"));
    if (j.project_id) loadProject(j.project_id);
  }, [reloadEnt, loadProject, t]);
  const poll = useJobPoller(onDone);

  useEffect(() => {
    if (!pid) { setProject(null); setJob(null); setFileId(null); setName(""); setS({ ...DEFAULTS, label_lang: langRef.current }); return; }
    loadProject(pid).then((d) => {
      if (d.job) { setJob(d.job); if (!TERMINAL.includes(d.job.status)) poll(d.job.id, setJob); }
    }).catch(() => setParams({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid, tool]);

  const set = (patch) => setS((prev) => ({ ...prev, ...patch }));
  const running = job && !TERMINAL.includes(job.status);
  const sourceChanged = project && fileId !== project.source_file_id;

  const generate = async () => {
    if (!fileId) return toast.error(t("tool.upload"));
    setBusy(true);
    try {
      let data;
      if (project && !sourceChanged) {
        ({ data } = await api.post(`/projects/${project.id}/generate`, { settings: s }));
      } else {
        ({ data } = await api.post("/projects", { name, tool, source_file_id: fileId, settings: s }));
        setParams({ project: data.project.id }, { replace: true });
        setProject(data.project);
      }
      setJob(data.job);
      poll(data.job.id, setJob);
      reloadEnt();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    try { await api.post(`/jobs/${job.id}/cancel`); setJob({ ...job, status: "canceled" }); reloadEnt(); } catch (e) { toast.error(errMsg(e)); }
  };

  const free = tool === "dimensions" && !s.remove_bg;
  const blocked = ent && !free && !ent.can_generate;

  return (
    <div data-testid={`tool-page-${tool}`}>
      <PageHeader title={t(`tools.${tool}.name`)} sub={t(`tools.${tool}.desc`)}>
        {project && <Link to={`/app/${tool}`} className="text-sm text-neutral-400 hover:text-white" data-testid="new-project-link">+ {t("tool.newProject")}</Link>}
      </PageHeader>
      <div className="grid lg:grid-cols-[380px_1fr] gap-8 items-start">
        <section className="panel p-5 space-y-5 lg:sticky lg:top-6" data-testid="tool-config-panel">
          <UploadBox fileId={fileId} onUploaded={setFileId} />
          <Field label={t("tool.projectName")}><Input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} disabled={!!project && !sourceChanged} data-testid="project-name-input" /></Field>
          <Options s={s} set={set} config={config} />
          {blocked ? (
            <Link to="/app/subscription" className="btn-brand w-full rounded-full py-3 font-semibold flex justify-center" data-testid="tool-subscribe-btn">{t("app.subscribeCta")}</Link>
          ) : (
            <button onClick={generate} disabled={busy || running || !fileId} data-testid="generate-btn"
              className="btn-brand w-full rounded-full py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
              {busy || running ? <Loader2 size={17} className="animate-spin" /> : project?.results?.length ? <RefreshCw size={17} /> : <Wand2 size={17} />}
              {running ? t("tool.generating") : project?.results?.length && !sourceChanged ? t("tool.regenerate") : t("tool.generate")}
            </button>
          )}
          <p className="text-[11px] text-neutral-500 text-center">{free ? t("tool.freeNote") : t("tool.creditNote")}</p>
        </section>
        <section className="space-y-4" data-testid="tool-result-panel">
          {job && (
            <div className="flex items-center gap-3 text-sm" data-testid="job-status">
              <StatusBadge status={job.status} />
              {running && <span className="text-neutral-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" />{t("tool.generating")}</span>}
              {job.status === "queued" && <button onClick={cancel} className="text-xs text-neutral-400 underline" data-testid="cancel-job-btn">{t("common.cancel")}</button>}
              {job.status === "failed" && (
                <>
                  <span className="text-xs text-red-300" data-testid="job-error">{t("errors")[job.error] ? `${t(`errors.${job.error}`)} ` : ""}{t("errors.GEN_FAILED")}</span>
                  <button onClick={generate} className="text-xs text-emerald-400 underline" data-testid="retry-job-btn">{t("common.retry")}</button>
                </>
              )}
            </div>
          )}
          {project?.results?.length ? (
            <ResultView project={project} tool={tool} onRefresh={() => loadProject(project.id)} />
          ) : (
            <div className="light-panel min-h-[420px] grid place-items-center text-center p-10" style={{ background: "#F8FAFC" }} data-testid="result-empty">
              <div className="text-neutral-500 flex flex-col items-center gap-3">
                {running ? <Loader2 className="animate-spin text-emerald-600" size={32} /> : <ImageOff size={30} />}
                <p className="text-sm max-w-xs">{running ? t("tool.generating") : t("tool.emptyResult")}</p>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
